import * as Popover from "@radix-ui/react-popover";
import classNames from "classnames";
import { AddCommentPop } from "components/AddComment/AddComment";
import { AddCommentPopoverRoot } from "components/AddCommentPopoverRoot/AddCommentPopoverRoot";
import { CommentsSlider } from "components/CommentsSlider/CommentsSlider";
import ToolTip from "components/design/toolTip";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { SelectionCell } from "components/DesignSystem/SelectionCell/SelectionCell";
import { SelectionHeaderCell } from "components/DesignSystem/SelectionHeaderCell/SelectionHeaderCell";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { LockedUi } from "components/LockedUi/LockedUi";
import Modal from "components/DesignSystem/Modal/Modal";
import JEStatusUpdateModal from "./JEStatusUpdateModal";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import Table from "components/DesignSystem/Table/V2/Table";
import { MagnifyingGlass } from "components/icons/MagnifyingGlass";
import { Recurring } from "components/icons/Recurring";
import { DeleteJournalEntryModal } from "components/JournalEntry/DeleteJournalEntryModal";
import { EditJournalEntry } from "components/JournalEntry/EditJournalEntry";
import { JournalEntrySlider } from "components/JournalEntry/JournalEntrySlider";
import { SliderAnimation } from "components/SliderAnimation/SliderAnimation";
import { DISPLAY_DATE, YYYY_MM_DD } from "constants/date";
import { ACCOUNTING_METHODS_OPTIONS } from "constants/accountMethod";
import { LEDGER_TYPE_OPTIONS, LEDGER_TYPES } from "constants/ledgerEntryTypes";
import dayjs from "dayjs";
import { AnimatePresence } from "framer-motion";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useFilters } from "hooks/useFilter";
import { ModalControl, useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { parse, stringify } from "qs";
import { MouseEvent, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  CellContext,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  Row,
  RowSelectionState,
  useReactTable,
} from "react-table-8.10.7";
import ThreeDots from "static/images/ThreeDots.svg";
import {
  useGetAllJournalEntriesQuery,
  useBulkUpdateJournalEntryStatusMutation,
  useLazyExportJournalEntriesQuery,
} from "store/apis/journalEntry";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { openLink } from "utils/openLink";
import { COMMENT_TYPE, NoteResponse } from "store/apis/notes";
import { setOpenComment } from "store/slices/openComment";
import { JournalEntry as JournalEntryType } from "types/Models/journalEntry";
import { debounce } from "utils/debouncing";
import { DuplicateJournalEntryModal } from "./DuplicateJournalEntry";
import { JournalEntryFilter } from "./JournalEntryFilter";
import TableSelectionControls from "./TableSelectionControls";
import {
  JESortColumnType,
  JOURNAL_ENTRY_SOURCE,
  JOURNAL_ENTRY_SOURCES_DISPLAY,
  JOURNAL_ENTRY_STATUS,
  JOURNAL_ENTRY_STATUS_DISPLAY,
  JOURNAL_ENTRY_STATUS_TYPE,
} from "constants/journalEntrySources";
import { MagicWand } from "components/icons/MagicWand";
import { ManualHand } from "components/icons/ManualHand";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { JournalEntrySortDropdown } from "./JournalEntrySortDropdown";
import { BackendError } from "types/utils/error";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { useToast } from "hooks/useToast";

const Action = ({ row, table }: CellContext<JournalEntryType, unknown>) => {
  const rowData = row.original;
  const { update } = useUpdateQuery();
  const query = useQuery();
  const editLedgerEntryId = query.get("edit_journal_entry_id");
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const [updateStatus] = useBulkUpdateJournalEntryStatusMutation();
  const [exportJournalEntries] = useLazyExportJournalEntriesQuery();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();

  const {
    isOpen: isOpenEdit,
    showConfirmation: showEditConfirmation,
    dismissConfirmation: dismissEditConfirmation,
    open: openEdit,
    close: closeEdit,
  } = useModal();
  const {
    isOpen: isOpenDuplicate,
    open: openDuplicate,
    close: closeDuplicate,
  } = useModal();

  const ledgerEntryId = rowData.uuid;
  const status = rowData.status;

  const {
    isOpen: isDeleteJournalEntryModalOpen,
    open: openDeleteJournalEntryModal,
    close: closeDeleteJournalEntryModal,
  } = useModal();

  const {
    isOpen: isArchiveModalOpen,
    open: openArchiveModal,
    close: closeArchiveModal,
  } = useModal();

  const {
    isOpen: isMarkActiveModalOpen,
    open: openMarkActiveModal,
    close: closeMarkActiveModal,
  } = useModal();

  const onEdit = () => {
    openEdit();
    update({
      query: "edit_journal_entry_id",
      value: ledgerEntryId,
    });
  };

  const onDelete = () => {
    openDeleteJournalEntryModal();
  };

  const onDuplicate = () => {
    openDuplicate();
  };

  const onDownload = async () => {
    try {
      const { document_id } = await exportJournalEntries({
        groupId,
        entityId,
        ledger_entry_id: ledgerEntryId,
      }).unwrap();

      const { download_url } = await getPreviewUrl({
        groupId,
        fileId: document_id,
      }).unwrap();

      openLink(download_url);
      successToast({ message: "Journal entry downloaded" });
    } catch (error) {
      alertToast({
        message: (error as BackendError).data?.error?.message,
      });
    }
  };

  const onEditClose = () => {
    closeEdit();
    update({
      query: "edit_journal_entry_id",
      value: null,
    });
  };

  const handleDeleteSuccess = () => {
    const onDeleteSuccess = (
      table.options.meta as { onDeleteSuccess?: (rowId: string) => void }
    )?.onDeleteSuccess;
    onDeleteSuccess?.(row.id);
  };

  const handleStatusUpdate = async (
    status: JOURNAL_ENTRY_STATUS_TYPE,
    onClose: () => void
  ) => {
    try {
      await updateStatus({
        groupId,
        entityId,
        ledger_entry_uuids: [ledgerEntryId],
        status,
      }).unwrap();
      successToast({
        message: `Journal entry ${JOURNAL_ENTRY_STATUS_DISPLAY[status]}.`,
      });
      onClose();
    } catch (error) {
      alertToast({
        message: (error as BackendError).data?.error?.message,
      });
    }
  };

  const isEditJEOpen = isOpenEdit || editLedgerEntryId === ledgerEntryId;
  const isActive = status === JOURNAL_ENTRY_STATUS.ACTIVE;

  return (
    <div className="t-text-text-30" onClick={(e) => e.stopPropagation()}>
      <div className="t-flex t-items-center t-h-full">
        <AddCommentPopoverRoot contentId={rowData.uuid}>
          <Dropdown.Root>
            <Dropdown.Trigger asChild>
              <div>
                <Popover.Anchor asChild>
                  <Button
                    size="small"
                    customType="ghost_icon"
                    title={`Action for ${rowData.entry_number}`}
                  >
                    <img
                      src={ThreeDots}
                      alt="Action"
                      className="t-select-none"
                    />
                  </Button>
                </Popover.Anchor>
              </div>
            </Dropdown.Trigger>
            <Popover.Portal>
              <AddCommentPop
                commentType={COMMENT_TYPE.LEDGER_ENTRY_COMMENT}
                contentId={rowData.uuid}
              />
            </Popover.Portal>
            <Dropdown.Portal>
              <Dropdown.Content sideOffset={8} side="bottom" align="end">
                {isActive ? (
                  <LockedUi feature={WRITE_PERMISSION_KEYS.ACCOUNTING}>
                    <Popover.Trigger asChild>
                      <Dropdown.Item>Add comment</Dropdown.Item>
                    </Popover.Trigger>
                    <Dropdown.Item onSelect={onDuplicate}>
                      Duplicate
                    </Dropdown.Item>
                    <Dropdown.Item onSelect={onEdit}>Edit</Dropdown.Item>
                    <Dropdown.Item onSelect={openArchiveModal}>
                      Archive
                    </Dropdown.Item>
                  </LockedUi>
                ) : (
                  <LockedUi feature={WRITE_PERMISSION_KEYS.ACCOUNTING}>
                    <Dropdown.Item onSelect={openMarkActiveModal}>
                      Mark as active
                    </Dropdown.Item>
                  </LockedUi>
                )}
                <Dropdown.Item onSelect={onDownload}>Download</Dropdown.Item>
                <LockedUi feature={WRITE_PERMISSION_KEYS.ACCOUNTING}>
                  <Dropdown.Item type="danger" onSelect={onDelete}>
                    Delete
                  </Dropdown.Item>
                </LockedUi>
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>
        </AddCommentPopoverRoot>
      </div>

      {isEditJEOpen && (
        <EditJournalEntry
          showConfirmation={showEditConfirmation}
          dismissConfirmation={dismissEditConfirmation}
          close={onEditClose}
          isOpen={isEditJEOpen}
          ledgerEntryId={rowData?.uuid}
        />
      )}
      {isOpenDuplicate && (
        <DuplicateJournalEntryModal
          close={closeDuplicate}
          isOpen={isOpenDuplicate}
          currentRowData={rowData!}
          ledgerEntryId={rowData?.uuid}
        />
      )}

      <DeleteJournalEntryModal
        ledgerEntryId={rowData?.uuid}
        close={closeDeleteJournalEntryModal}
        isOpen={isDeleteJournalEntryModalOpen}
        onDeleteSuccess={handleDeleteSuccess}
      />

      <Modal.Root open={isArchiveModalOpen} onOpenChange={closeArchiveModal}>
        <Modal.Content>
          <JEStatusUpdateModal
            title="Archive Journal Entry?"
            description="You are about to archive this journal entry. You can mark it as active again later."
            confirmLabel="Archive"
            onConfirm={() =>
              handleStatusUpdate(
                JOURNAL_ENTRY_STATUS.ARCHIVED,
                closeArchiveModal
              )
            }
          />
        </Modal.Content>
      </Modal.Root>

      <Modal.Root
        open={isMarkActiveModalOpen}
        onOpenChange={closeMarkActiveModal}
      >
        <Modal.Content>
          <JEStatusUpdateModal
            title="Mark Journal Entry as Active?"
            description="You are about to mark this journal entry as active."
            confirmLabel="Mark as active"
            onConfirm={() =>
              handleStatusUpdate(
                JOURNAL_ENTRY_STATUS.ACTIVE,
                closeMarkActiveModal
              )
            }
          />
        </Modal.Content>
      </Modal.Root>
    </div>
  );
};

export const JournalEntry = ({
  commentPaneControl,
}: {
  commentPaneControl: ModalControl;
}) => {
  usePageTitle("Journal Entry");
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const query = useQuery();
  const pageNum = Number(query.get("page")) || 1;
  const searchTerm = query.get("search_term") || null;
  const pageSize = query.get("page_size");
  const { update, updateMultiple } = useUpdateQuery();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const activeJournalEntry = query.get("active_journal_entry");
  const openCommentParam = query.get("open_comment");
  const openComment = useAppSelector((state) => state.openComment.openComment);
  const journalEntryId = query.get("journal_entry_id");

  const dispatch = useDispatch();

  const setActiveComment = (comment: NoteResponse) => {
    if (comment.ledger_entry?.uuid) {
      update({
        query: "active_journal_entry",
        value: comment.ledger_entry?.uuid,
      });
    }
    if (comment.ledger_entry) {
      dispatch(setOpenComment(comment.ledger_entry.uuid));
    }
  };

  const [sorting, setSorting] = useState<
    {
      desc: boolean;
      id: JESortColumnType;
    }[]
  >([
    {
      id: "DATE",
      desc: true,
    },
  ]);

  const sortCol = sorting[0]?.id;
  const sortOrder = sorting[0]?.desc;

  const { values, updateFilter } = useFilters({
    initialValue: {
      START_DATE: "",
      END_DATE: "",
      SELECT_PERIOD: "" as string,
      LEDGER_TYPES: [] as string[],
      ACCOUNTING_METHOD: [] as string[],
      SOURCE: [] as string[],
      RECURRING_ENTRIES_ONLY: false,
      HIDE_FUTURE_ENTRIES: true,
      STATUS: [JOURNAL_ENTRY_STATUS.ACTIVE] as string[],
    },
    useQueryParams: true,
  });

  const params = {
    pageNum,
    searchTerm,
    startDate: values.START_DATE
      ? dayjs(values.START_DATE).format(YYYY_MM_DD)
      : null,
    endDate: values.END_DATE ? dayjs(values.END_DATE).format(YYYY_MM_DD) : null,
    sortCol: sortCol,
    sortOrder: sortOrder ? ("DSC" as const) : ("ASC" as const),
    ledger_types: values.LEDGER_TYPES.join(",") || null,
    accounting_method: values.ACCOUNTING_METHOD.join(",") || null,
    source: values.SOURCE.join(",") || null,
    recurring_entries_only: values.RECURRING_ENTRIES_ONLY || null,
    hide_future_entries: values.HIDE_FUTURE_ENTRIES || null,
    status: values.STATUS.join(",") || null,
    pageSize: pageSize ? Number(pageSize) : undefined,
  };

  const { data, isLoading, isSuccess, isFetching } =
    useGetAllJournalEntriesQuery(
      {
        groupId,
        entityId,
        ...params,
      },
      { skip: !groupId || !entityId }
    );

  const {
    journal_entries = [],
    per_page = 1,
    total_count = 1,
    total_pages = 1,
  } = data || {};

  const closeJournalEntrySlider = () => {
    update({
      query: "journal_entry_id",
      value: null,
    });
  };

  const handleDeleteSuccess = (rowId: string) => {
    setRowSelection((prev) => {
      const updated = { ...prev };
      delete updated[rowId];
      return updated;
    });
  };

  const columnHelper = createColumnHelper<JournalEntryType>();
  const hasSelection = Object.keys(rowSelection).length > 0;

  const columns = [
    columnHelper.display({
      id: "select",
      size: 2,
      header: ({ table }) => <SelectionHeaderCell table={table} />,
      cell: ({ row }) => (
        <SelectionCell
          row={row}
          hasSelection={hasSelection}
          visibility="hover"
        />
      ),
    }),

    columnHelper.accessor("date", {
      id: "DATE",
      size: 12,
      header: () => <HeadCellContent title="Entry date" />,
      enableSorting: false,
      cell: (info) => (
        <div className="t-text-text-60">
          {info.getValue() ? dayjs(info.getValue()).format(DISPLAY_DATE) : "-"}
        </div>
      ),
    }),

    columnHelper.accessor("source", {
      size: 7,
      header: () => <HeadCellContent title="Source" align="center" />,
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return value ? (
          <div className="t-text-text-30 t-flex t-justify-center">
            <ToolTip text={JOURNAL_ENTRY_SOURCES_DISPLAY[value]}>
              <span>
                {value === JOURNAL_ENTRY_SOURCE.SYSTEM && <MagicWand />}
                {value === JOURNAL_ENTRY_SOURCE.MANUAL && <ManualHand />}
              </span>
            </ToolTip>
          </div>
        ) : (
          "-"
        );
      },
    }),

    columnHelper.accessor("entry_name", {
      id: "TITLE",
      size: 30,
      header: () => <HeadCellContent title="Title" />,
      enableSorting: false,
      cell: (info) => (
        <div className="t-flex t-gap-2">
          <div className="t-text-text-60">{info.getValue()}</div>
          {info.row.original.settings?.is_recurring_enabled && (
            <span className="t-text-text-30 t-flex t-items-center">
              <ToolTip text="Recurring journal entry" disableHoverableContent>
                <span>
                  <Recurring />
                </span>
              </ToolTip>
            </span>
          )}
        </div>
      ),
    }),

    columnHelper.accessor("ledger_type", {
      size: 20,
      header: () => <HeadCellContent title="Type" />,
      enableSorting: false,
      cell: (info) => (
        <div className="t-text-text-60">
          {LEDGER_TYPE_OPTIONS.find(
            (option) => option.value === info.getValue()
          )?.label || "-"}
          {info.getValue() === LEDGER_TYPES.OTHERS && (
            <div className="t-text-body-sm t-mt-1 t-text-text-30">
              {info.row.original.ledger_type_str}
            </div>
          )}
        </div>
      ),
    }),

    columnHelper.display({
      id: "basis",
      size: 16,
      header: () => <HeadCellContent title="Basis" />,
      enableSorting: false,
      cell: (info) => {
        const ledgerEntry = info.row.original;

        const basisDisplay = ledgerEntry.basis
          ? ACCOUNTING_METHODS_OPTIONS[ledgerEntry.basis]
          : "Mixed";

        return <div className="t-text-text-60">{basisDisplay}</div>;
      },
    }),

    columnHelper.accessor("amount", {
      size: 10,
      header: () => <HeadCellContent title="Amount" align="right" />,
      enableSorting: false,
      cell: (info) => (
        <div className="t-text-text-60 t-text-right">
          <AmountSuperScript amount={Number(info.getValue() || 0)} />
        </div>
      ),
    }),

    columnHelper.display({
      id: "action",
      size: 15,
      header: () => <HeadCellContent title="" />,
      cell: Action,
    }),
  ];

  const table = useReactTable({
    data: journal_entries || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    manualSorting: true,
    enableMultiSort: false,
    enableSortingRemoval: false,
    // @ts-ignore
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      rowSelection,
    },
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
    getRowId: (row) =>
      JSON.stringify({
        uuid: row.uuid,
        is_recurring_enabled: row.settings?.is_recurring_enabled || false,
        linked_payroll_id: row.linked_payroll_id,
        status: row.status,
      }),
    meta: {
      onDeleteSuccess: handleDeleteSuccess,
    },
  });

  const handleChange = debounce((e) => {
    const { value } = e.target;
    updateMultiple([
      { query: "search_term", value: value || null },
      { query: "page", value: 1 },
    ]);
  });

  const onRowClick = (
    e: MouseEvent<HTMLTableRowElement>,
    row: { original: JournalEntryType }
  ) => {
    e.stopPropagation();
    update({
      query: "journal_entry_id",
      value: row.original?.uuid,
    });
  };

  const isEmpty = journal_entries.length === 0;

  const checkRef = (
    rowEl: HTMLTableRowElement | null,
    row: Row<JournalEntryType> | null
  ) => {
    if (row && row.original.uuid === activeJournalEntry && rowEl) {
      rowEl.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    let currentSearch = parse(location.search, { ignoreQueryPrefix: true });
    let timeout: NodeJS.Timeout;
    if (activeJournalEntry) {
      if (currentSearch) {
        delete currentSearch.active_journal_entry;
      }

      timeout = setTimeout(() => {
        history.replace(
          `${location.pathname}${stringify(currentSearch, {
            addQueryPrefix: true,
          })}`
        );
      }, 5000);
    }

    return () => clearTimeout(timeout);
  }, [history, location.pathname, location.search, activeJournalEntry]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (openCommentParam) {
      dispatch(setOpenComment(openCommentParam));
      const currentSearch = parse(location.search, { ignoreQueryPrefix: true });
      delete currentSearch.open_comment;
      timeout = setTimeout(() => {
        history.replace(
          `${location.pathname}${stringify(currentSearch, {
            addQueryPrefix: true,
          })}`
        );
      }, 5000);
    }
    return () => clearTimeout(timeout);
  }, [openCommentParam]);

  return (
    <div className="t-flex t-h-full t-pl-5 sm:t-h-auto sm:t-pl-3 sm:t-w-full sm:t-max-w-full sm:t-overflow-x-hidden">
      <div className="t-flex t-flex-col t-gap-5 t-flex-1 t-relative t-overflow-hidden sm:t-overflow-x-hidden sm:t-overflow-y-visible sm:t-min-w-0 sm:t-max-w-full">
        <div
          className={classNames(
            "t-flex t-flex-col t-gap-5 t-z-header t-bg-white",
            {
              "t-pr-3": commentPaneControl.isOpen,
              "t-pr-10 sm:t-pr-3": !commentPaneControl.isOpen,
            }
          )}
        >
          <div className="t-w-full t-flex t-gap-2 sm:t-flex-col">
            <div className="t-flex t-gap-2 t-flex-1 t-flex-wrap t-pl-1 sm:t-flex-nowrap sm:t-pl-0 sm:t-items-center">
              <Search
                isLoading={isFetching}
                className="t-w-[18%] focus:t-w-3/6 t-transition-all sm:t-w-auto sm:t-min-w-0 sm:t-flex-1 sm:focus:t-w-auto"
                placeholder="Search..."
                defaultValue={searchTerm || ""}
                onChange={handleChange}
              />
              <JournalEntrySortDropdown
                sorting={sorting}
                setSorting={setSorting}
              />
              <JournalEntryFilter values={values} updateFilter={updateFilter} />
            </div>

            <div className="t-ml-auto t-shrink-0 t-flex t-justify-end sm:t-ml-0 sm:t-self-end">
              <PaginationHandler
                page_num={pageNum}
                per_page={per_page}
                total_count={total_count}
                total_pages={total_pages}
                isLoading={isFetching}
                showOptions
              />
            </div>
          </div>
        </div>
        <Async.Root {...{ isLoading, isEmpty, isSuccess }}>
          <Async.Empty>
            <EmptyScreen text="No journal entry found">
              <span className="t-text-i-neutral-10">
                <MagnifyingGlass size="149" />
              </span>
            </EmptyScreen>
          </Async.Empty>
          <Async.Success>
            <Table.Container
              sticky
              className="t-h-full t-overflow-y-auto t-pb-32 t-rounded-tl-lg t-rounded-tr-lg t-mr-10 sm:t-h-auto sm:t-mr-0 sm:t-overflow-y-visible sm:t-overflow-x-auto"
            >
              <Table.Content
                className={classNames({
                  "t-pr-3": commentPaneControl.isOpen,
                  "t-pr-10 sm:t-pr-3 sm:t-min-w-[800px]":
                    !commentPaneControl.isOpen,
                })}
              >
                <Table.Head>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <Table.Row key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <Table.HeadCell
                          isUpperCase={false}
                          key={header.id}
                          style={{ width: `${header.getSize()}%` }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </Table.HeadCell>
                      ))}
                    </Table.Row>
                  ))}
                </Table.Head>
                <Table.Body>
                  {table.getRowModel().rows.map((row) => {
                    const isArchived =
                      row.original.status === JOURNAL_ENTRY_STATUS.ARCHIVED;

                    return (
                      <ConditionalToolTip
                        key={row.id}
                        condition={
                          isArchived && (
                            <div className="t-w-60">
                              This JE is archived. Mark it as active to make any
                              changes.
                            </div>
                          )
                        }
                        side="top"
                      >
                        <Table.Row
                          ref={(rowEl) => checkRef(rowEl, row)}
                          onClick={(e) => onRowClick(e, row)}
                          isRowSelected={row.getIsSelected()}
                          className={classNames("t-group t-cursor-pointer", {
                            "hover:t-bg-surface-lighter-grey ": !isArchived,
                            "animate-transaction-row":
                              row.original.uuid === activeJournalEntry,
                            "t-bg-surface-lighter-grey":
                              row.original.uuid === journalEntryId,
                          })}
                          data-testid={`journal-entry-row-${row.index}`}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <Table.Cell
                              id={cell.id}
                              key={cell.id}
                              style={{ width: `${cell.column.getSize()}%` }}
                              className={classNames({
                                "t-opacity-50":
                                  isArchived &&
                                  cell.column.id !== "action" &&
                                  cell.column.id !== "select",
                              })}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </Table.Cell>
                          ))}
                        </Table.Row>
                      </ConditionalToolTip>
                    );
                  })}
                </Table.Body>
              </Table.Content>
            </Table.Container>
          </Async.Success>
        </Async.Root>
        <TableSelectionControls
          table={table}
          groupId={groupId}
          entityId={entityId}
        />
        {journalEntryId && (
          <JournalEntrySlider
            isOpen={Boolean(journalEntryId)}
            onClose={closeJournalEntrySlider}
            ledgerEntryId={journalEntryId}
          />
        )}
      </div>
      <AnimatePresence>
        {commentPaneControl.isOpen && (
          <SliderAnimation className="t-h-full t-shrink-0 t-grow-0 t-sticky t-top-0 t-border-0 t-border-l t-border-solid t-border-neutral-0 t-overflow-y-auto t-p-4 t-pt-0 sm:t-fixed sm:t-inset-0 sm:t-z-modal sm:t-bg-surface sm:!t-w-full">
            <CommentsSlider
              activeComment={openComment}
              setActiveComment={setActiveComment}
              journalEntryFilters={params}
              onClose={commentPaneControl.toggle}
              commentTypes={[COMMENT_TYPE.LEDGER_ENTRY_COMMENT]}
            />
          </SliderAnimation>
        )}
      </AnimatePresence>
    </div>
  );
};
